import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Brain,
  Heart,
  Sparkles,
  BookOpen,
  MapPin
} from 'lucide-react';
import SEO from '../components/SEO';
import RelatedPages from '../components/RelatedPages';
import { relatedPagesMap } from '../data/relatedPages';
import FAQ from '../components/FAQ';
import { faqMap } from '../data/faqContent';

const principles = [
  {
    title: "EMA plutôt qu'EMC",
    body:
      "L'Hypnopraxie® s'adresse davantage à l'EMA (état modifié d'affect) qu'à l'EMC (état modifié de conscience). Là où l'hypnose classique travaille sur la conscience, l'Hypnopraxie® travaille sur l'affect — sur ce qui est ressenti au plus intime."
  },
  {
    title: "Une thérapie de l'état d'ÂME",
    body:
      "L'objectif est une maturation affective de ce qui est éprouvé en SOI. Il s'agit d'une thérapie de la maturation de l'être humain, qui accompagne le vécu émotionnel profond."
  },
  {
    title: "La chair et la somatisation",
    body:
      "Ce qui est éprouvé concerne la CHAIR — les organes, tissus et cellules. La somatisation se produit lorsque nous éprouvons intensément des expériences sans pouvoir les intégrer."
  },
  {
    title: "Une thérapie intégrative",
    body:
      "L'Hypnopraxie® cherche l'unité corps-esprit de l'être humain. Elle ne sépare pas le mental du physique, l'émotionnel du corporel."
  }
];

const indications = [
  'Troubles alimentaires et rapport au corps difficile',
  'Compulsions (sucre, nourriture, comportements)',
  'Anxiété et stress chronique',
  'Somatisations (douleurs, troubles digestifs, fatigue inexpliquée)',
  'Blessures émotionnelles anciennes',
  'Perte de poids lorsque les émotions y sont liées',
  'Accompagnement en préménopause, SOPK (syndrome des ovaires polykystiques), troubles hormonaux'
];

const HypnopraxieVevey = () => {
  return (
    <>
      <SEO
        title="Hypnopraxie® à Vevey & Montreux | Thérapie intégrative corps-esprit"
        description="Hypnopraxie® sur la Riviera vaudoise (Vevey, Clarens, Montreux). Méthode avancée de l'hypnose créée par le Dr Guy Chedeau, thérapie intégrative corps-esprit pour la maturation affective."
        keywords="hypnopraxie Vevey, hypnopraxie Montreux, hypnopraxie Riviera, thérapie intégrative Vevey, Dr Guy Chedeau, EMA état modifié d'affect, hypnose Vevey, somatisation Riviera, thérapie corps-esprit Montreux"
        url="https://nutricentreriviera.ch/hypnopraxie-vevey-montreux"
      />

      <div data-testid="page-hypnopraxie-vevey" style={{ marginTop: '100px' }}>
        {/* Hero */}
        <section
          style={{
            background: 'var(--gradient-hero)',
            padding: '4rem 0',
            textAlign: 'center'
          }}
        >
          <div className="container">
            <h1 className="heading-hero" style={{ marginBottom: '1.5rem' }}>
              Hypnopraxie® à Vevey & Montreux
            </h1>
            <p
              className="heading-subtitle"
              style={{ maxWidth: '900px', margin: '0 auto 2rem' }}
            >
              Une thérapie intégrative corps-esprit, pour une maturation affective profonde, sur la Riviera vaudoise.
            </p>
            <a
              href="https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{ textDecoration: 'none' }}
              data-testid="hypnopraxie-hero-cta"
            >
              Prendre rendez-vous
              <ArrowRight size={20} />
            </a>
          </div>
        </section>

        {/* Quote */}
        <section style={{ background: 'white', paddingTop: '3rem' }}>
          <div className="container" style={{ maxWidth: '900px' }}>
            <blockquote
              style={{
                borderLeft: '3px solid var(--accent-primary)',
                paddingLeft: '1.5rem',
                margin: '0',
                fontStyle: 'italic',
                color: 'var(--accent-primary)',
                fontSize: '1.25rem',
                lineHeight: 1.6
              }}
            >
              « L'esprit ne voit que ce que le cœur lui montre »
            </blockquote>
          </div>
        </section>

        {/* Introduction */}
        <section className="section" style={{ background: 'white' }}>
          <div className="container" style={{ maxWidth: '900px' }}>
            <div style={{ marginBottom: '4rem' }}>
              <h2
                className="heading-2"
                style={{ marginBottom: '1.5rem', color: 'var(--accent-primary)' }}
              >
                Qu'est-ce que l'Hypnopraxie® ?
              </h2>
              <p
                className="body-large"
                style={{ marginBottom: '1rem', lineHeight: 1.8 }}
              >
                L'<strong>Hypnopraxie®</strong> est une <strong>méthode avancée de l'hypnose incluant une variable affective</strong>.
                Présentée pour la première fois en 2005 à Buenos Aires, au congrès mondial des
                psychothérapeutes, elle a été créée par le <strong>docteur Guy Chedeau</strong> pour garder le lien à l'hypnose.
              </p>
              <p
                className="body-large"
                style={{ marginBottom: '1rem', lineHeight: 1.8 }}
              >
                La « <strong>Praxie</strong> » signifie la manière personnelle qu'a un individu
                d'expérimenter le moment présent. De sa manière de percevoir la situation et
                d'interagir avec ce qui se passe découle une attitude, une manière de se
                comporter. Face à une même situation, chacun perçoit le monde de manière unique
                et va agir en fonction de l'« <strong>Éprouver</strong> » du moment.
              </p>
              <p className="body-large" style={{ lineHeight: 1.8 }}>
                Ces praxies sont liées au <strong>sentiment de soi</strong> qui mûrit tout au
                long de notre existence. L'Hypnopraxie® est une <strong>thérapie de la maturation de l'être humain</strong>, avec l'intention de
                donner une base phénoménologique à l'hypnose, à la psychopathologie et à la
                physiopathologie hypnotique.
              </p>
            </div>

            {/* Les principes fondamentaux */}
            <div style={{ marginBottom: '4rem' }}>
              <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                Les 4 principes fondamentaux
              </h2>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: '1.5rem'
                }}
              >
                {principles.map((p, i) => (
                  <div
                    key={i}
                    className="principle-card"
                    style={{
                      background: 'white',
                      borderRadius: '1rem',
                      padding: '1.75rem',
                      border: '1px solid var(--border-light)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <div
                      style={{
                        width: '2.75rem',
                        height: '2.75rem',
                        borderRadius: '0.75rem',
                        background: 'var(--accent-primary-light)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '1rem'
                      }}
                    >
                      {i === 0 && (
                        <Brain size={22} style={{ color: 'var(--accent-primary)' }} />
                      )}
                      {i === 1 && (
                        <Sparkles size={22} style={{ color: 'var(--accent-primary)' }} />
                      )}
                      {i === 2 && (
                        <Heart size={22} style={{ color: 'var(--accent-primary)' }} />
                      )}
                      {i === 3 && (
                        <CheckCircle2
                          size={22}
                          style={{ color: 'var(--accent-primary)' }}
                        />
                      )}
                    </div>
                    <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>
                      {p.title}
                    </h3>
                    <p className="body-medium" style={{ lineHeight: 1.7 }}>
                      {p.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Pour qui ? */}
            <div style={{ marginBottom: '4rem' }}>
              <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                Pour qui et pour quoi ?
              </h2>
              <div
                style={{
                  background: 'var(--bg-page)',
                  borderRadius: '1rem',
                  padding: '2rem',
                  border: '1px solid var(--border-light)'
                }}
              >
                <p className="body-large" style={{ marginBottom: '1.5rem' }}>
                  L'Hypnopraxie® peut accompagner de nombreuses problématiques, particulièrement
                  lorsqu'elles portent une dimension émotionnelle profonde :
                </p>
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.85rem'
                  }}
                >
                  {indications.map((item, i) => (
                    <li
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.85rem'
                      }}
                    >
                      <CheckCircle2
                        size={22}
                        style={{
                          color: 'var(--accent-primary)',
                          flexShrink: 0,
                          marginTop: '0.2rem'
                        }}
                      />
                      <span className="body-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Approche combinée */}
            <div style={{ marginBottom: '4rem' }}>
              <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                Une approche intégrée à la santé féminine
              </h2>
              <div
                style={{
                  background:
                    'linear-gradient(135deg, var(--accent-primary-light) 0%, rgba(200, 230, 216, 0.3) 100%)',
                  borderRadius: '1rem',
                  padding: '2rem',
                  border: '1px solid var(--accent-primary)',
                  borderLeft: '4px solid var(--accent-primary)'
                }}
              >
                <p className="body-large" style={{ lineHeight: 1.8, marginBottom: '1rem' }}>
                  Au Nutri Centre Riviera, l'Hypnopraxie® s'intègre dans une prise en charge
                  globale de la santé féminine. L'équilibre hormonal, métabolique et émotionnel
                  ne peuvent être dissociés — l'Hypnopraxie® vient compléter l'accompagnement
                  nutritionnel en touchant à ce que les seuls conseils alimentaires ne peuvent
                  atteindre : <strong>l'éprouvé intime</strong>.
                </p>
                <p className="body-large" style={{ lineHeight: 1.8 }}>
                  Elle peut notamment soutenir les accompagnements en <strong>perte de poids</strong>, <strong>SOPK (syndrome des ovaires polykystiques)</strong>, <strong>préménopause</strong> et <strong>compulsions alimentaires</strong>,
                  lorsque la dimension émotionnelle est au cœur du blocage.
                </p>
              </div>
            </div>

            {/* Localisation */}
            <div style={{ marginBottom: '3rem' }}>
              <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                Consultations sur la Riviera vaudoise
              </h2>
              <p className="body-large" style={{ marginBottom: '1.5rem', lineHeight: 1.8 }}>
                Les séances d'Hypnopraxie® sont disponibles en présentiel à <strong>Vevey</strong> et à <strong>Clarens (Montreux)</strong>, dans un cadre
                professionnel au sein d'un cabinet médical.
              </p>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem 1.25rem',
                  background: 'var(--bg-page)',
                  borderRadius: '0.75rem',
                  borderLeft: '4px solid var(--accent-primary)'
                }}
              >
                <MapPin size={20} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
                <span className="body-large" style={{ fontWeight: 500 }}>
                  Cabinets à Vevey et Clarens (Montreux), facilement accessibles depuis toute la
                  Riviera vaudoise.
                </span>
              </div>
            </div>

            {/* Lien vers l'article de blog */}
            <div
              style={{
                marginTop: '3rem',
                padding: '1.75rem',
                background: 'white',
                border: '1px solid var(--border-light)',
                borderRadius: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                flexWrap: 'wrap'
              }}
            >
              <BookOpen size={28} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: '250px' }}>
                <p className="body-medium" style={{ marginBottom: '0.25rem', fontWeight: 600 }}>
                  Envie d'approfondir ?
                </p>
                <p className="body-small" style={{ color: 'var(--text-muted)' }}>
                  Découvrez notre article de blog complet sur l'Hypnopraxie®.
                </p>
              </div>
              <Link
                to="/blog/hypnopraxie-therapie-integrative"
                className="btn-secondary"
                style={{
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                data-testid="link-to-blog-hypnopraxie"
              >
                Lire l'article
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>

        <FAQ items={faqMap['hypnopraxie-vevey-montreux']} />

        <RelatedPages items={relatedPagesMap['hypnopraxie-vevey-montreux']} />

        {/* CTA final */}
        <section
          style={{
            background: 'var(--accent-primary)',
            color: 'white',
            padding: '5rem 0'
          }}
        >
          <div className="container" style={{ textAlign: 'center' }}>
            <h2 className="heading-1" style={{ color: 'white', marginBottom: '1rem' }}>
              Entretien découverte de 15 minutes offert
            </h2>
            <p
              className="body-large"
              style={{
                color: 'white',
                opacity: 0.95,
                maxWidth: '700px',
                margin: '0 auto 2.5rem'
              }}
            >
              Discutons ensemble pour savoir si l'Hypnopraxie® peut vous accompagner.
            </p>
            <a
              href="https://www.onedoc.ch/fr/therapeute-en-nutrition-mco/vevey/pcyv4/zara-nawid"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{
                background: 'white',
                color: 'var(--accent-primary)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                textDecoration: 'none'
              }}
              data-testid="hypnopraxie-final-cta"
            >
              Réserver un rendez-vous
              <ArrowRight size={20} />
            </a>
          </div>
        </section>

        <style>{`
          .principle-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 24px rgba(61, 84, 70, 0.15);
            border-color: var(--accent-primary);
          }
        `}</style>
      </div>
    </>
  );
};

export default HypnopraxieVevey;
